import type { CollectionItemKind, CollectionItemStatus, SignaturePath } from "../api/types";
import { StatusStepper } from "@/design-system/components/ui";
import { signingRouteFor } from "../lib/signingRoute";

// CF-4 P2 — per-item step indicator. Tells the recipient where they are
// in the fulfilment flow (Provide → Review → Sign → Done) so a multi-step
// item never feels like a single opaque page. Sign step only appears when
// the item actually requires a signature — read from the server-enforced
// route (`resolvedSignaturePath`), the same one the surfaces follow.

interface StepIndicatorProps {
  kind: CollectionItemKind;
  status: CollectionItemStatus;
  requiresSignature: boolean;
  signaturePath: SignaturePath | null;
  /** The route the server enforces; when omitted (older payloads) the
   *  previous requiresSignature + signaturePath rule applies. */
  resolvedSignaturePath?: SignaturePath | null;
}

type StepKey = "provide" | "review" | "sign" | "done" | "learn";

interface Step {
  key: StepKey;
  label: string;
}

const LABELS: Record<StepKey, string> = {
  provide: "Provide",
  review: "Review",
  sign: "Sign",
  done: "Done",
  learn: "Learn",
};

function buildSteps(
  kind: CollectionItemKind,
  hasSignature: boolean,
): Step[] {
  if (kind === "ESign") {
    return [
      { key: "sign", label: LABELS.sign },
      { key: "review", label: LABELS.review },
      { key: "done", label: LABELS.done },
    ];
  }
  if (kind === "Education") {
    return [
      { key: "learn", label: LABELS.learn },
      { key: "done", label: LABELS.done },
    ];
  }
  // Form / Upload — Sign step only when the template requires it
  // (Form + InHouse signature, currently the only combo).
  const base: Step[] = [{ key: "provide", label: LABELS.provide }];
  if (hasSignature) {
    base.push({ key: "review", label: LABELS.review });
    base.push({ key: "sign", label: LABELS.sign });
  } else {
    base.push({ key: "review", label: LABELS.review });
  }
  base.push({ key: "done", label: LABELS.done });
  return base;
}

function currentStepIndex(steps: Step[], status: CollectionItemStatus): number {
  // Pending/InProgress → first non-done step; SubmittedForReview → next-to-
  // last (review/sign already happened, awaiting reviewer); Approved/Waived
  // → Done. ChangesRequested → back to the first step.
  if (status === "Approved" || status === "Waived") {
    return steps.length - 1;
  }
  if (status === "SubmittedForReview") {
    // The penultimate step ("Review" by reviewer staff). If the flow has
    // a Sign step the user just completed, "Review" still represents the
    // reviewer's check that happens afterwards.
    const reviewIdx = steps.findIndex((s) => s.key === "review");
    return reviewIdx >= 0 ? reviewIdx : Math.max(0, steps.length - 2);
  }
  // Pending / InProgress / ChangesRequested
  return 0;
}

export function StepIndicator({
  kind,
  status,
  requiresSignature,
  signaturePath,
  resolvedSignaturePath,
}: StepIndicatorProps) {
  const hasSignature =
    (kind === "Form" || kind === "Upload") &&
    signingRouteFor({ kind, requiresSignature, signaturePath, resolvedSignaturePath }) === "InHouse";
  const steps = buildSteps(kind, hasSignature);
  const currentIdx = currentStepIndex(steps, status);
  const isDone = status === "Approved" || status === "Waived";

  // Domain logic above stays here; the visual treatment is the shared kit's
  // StatusStepper so every portal lifecycle indicator looks identical.
  return (
    <StatusStepper
      steps={steps}
      currentIndex={currentIdx}
      complete={isDone}
      data-testid="step-indicator"
    />
  );
}
